import type { Dispatch, FC, FormEventHandler, SetStateAction } from 'react';
import { formatCurrency } from '../lib/format';

export interface RecruitingDepartment {
  id: number | string;
  name: string;
}

export interface RecruitingPosition {
  id: number | string;
  title: string;
  job_code: string;
}

export interface RecruitingRequisition {
  id: number;
  slug: string;
  title: string;
  department_name?: string | null;
  position_title?: string | null;
  employment_type: string;
  location?: string | null;
  headcount: number;
  status: string;
  budgeted_salary_min: number;
  budgeted_salary_max: number;
  target_start_date?: string | null;
  opened_at?: string | null;
  closed_at?: string | null;
}

export interface RecruitingCandidate {
  id: number;
  full_name: string;
  email: string;
  phone?: string | null;
  source?: string | null;
  current_company?: string | null;
  current_title?: string | null;
}

export interface RecruitingApplication {
  id: number;
  candidate_id: number;
  candidate_name: string;
  candidate_email: string;
  requisition_id: number;
  requisition_title: string;
  stage: string;
  status: string;
  score?: number | null;
  notes_summary?: string | null;
  interview_scheduled_at?: string | null;
  evaluator_notes?: string | null;
  rejection_reason?: string | null;
}

export interface RecruitingOffer {
  id: number;
  application_id: number;
  application_stage: string;
  candidate_id: number;
  candidate_name: string;
  candidate_email: string;
  requisition_id: number;
  requisition_title: string;
  status: string;
  compensation_amount: number;
  currency: string;
  proposed_start_date?: string | null;
  expires_at?: string | null;
  notes_summary?: string | null;
  message?: string | null;
  send_approval_request_id?: number | null;
  send_approval_status?: string | null;
  send_approval_decision_reason?: string | null;
  onboarding_handoff_id?: number | null;
  employee_id?: number | null;
  employee_code?: string | null;
  sent_at?: string | null;
  responded_at?: string | null;
}

export interface RecruitingOnboardingHandoff {
  id: number;
  offer_id: number;
  application_id: number;
  candidate_id: number;
  candidate_name: string;
  candidate_email: string;
  requisition_id: number;
  requisition_title: string;
  employee_id?: number | null;
  employee_code?: string | null;
  provisioned_user_id?: number | null;
  provisioned_username?: string | null;
  status: string;
  target_start_date?: string | null;
  owner_role: string;
  notes_summary?: string | null;
}

export interface OnboardingChecklistTask {
  id: number;
  handoff_id: number;
  employee_id: number;
  title: string;
  is_required?: boolean;
  acknowledgement_statement?: string | null;
  acknowledgement_version?: string | null;
  status: string;
  due_date?: string | null;
  acknowledged_at?: string | null;
  acknowledged_signature_name?: string | null;
  completed_at?: string | null;
}

export interface OnboardingDocument {
  id: number;
  task_id?: number | null;
  document_name: string;
  file_name: string;
  content_type: string;
  size_bytes: number;
  created_at: string;
}

export interface OnboardingChecklistTemplate {
  id?: number | null;
  title: string;
  due_days_offset?: number | string | null;
  sort_order: number;
  is_required: boolean;
  acknowledgement_statement?: string | null;
  acknowledgement_version?: string | null;
}

export interface OnboardingSummaryItem {
  handoff_id: number;
  candidate_name: string;
  requisition_title: string;
  status: string;
  employee_code?: string | null;
  provisioned_username?: string | null;
  task_total: number;
  task_completed: number;
  document_total: number;
  overdue_task_count: number;
  required_acknowledgements_pending: number;
  required_acknowledgements_completed: number;
}

export interface OnboardingSummary {
  total_handoffs: number;
  completed_handoffs: number;
  provisioned_users: number;
  total_tasks: number;
  completed_tasks: number;
  total_documents: number;
  overdue_tasks: number;
  required_acknowledgements_pending: number;
  required_acknowledgements_completed: number;
  items: OnboardingSummaryItem[];
}

export interface OnboardingTaskFormState {
  title: string;
  due_date: string;
}

export interface OnboardingUserFormState {
  username: string;
  password: string;
  full_name: string;
}

export interface OnboardingDocumentFormState {
  document_name: string;
  task_id: string;
  file: File | null;
}

export interface RecruitingFormState {
  slug: string;
  title: string;
  department_id: string;
  position_id: string;
  employment_type: string;
  location: string;
  headcount: string | number;
  budgeted_salary_min: string | number;
  budgeted_salary_max: string | number;
  target_start_date: string;
  description: string;
}

export interface CandidateFormState {
  full_name: string;
  email: string;
  phone: string;
  source: string;
  current_company: string;
  current_title: string;
  resume_url: string;
}

export interface ApplicationFormState {
  requisition_id: string;
  candidate_id: string;
  score: string | number;
  notes_summary: string;
}

export interface ApplicationStageFormState {
  application_id: string;
  stage: string;
  score: string | number;
  notes_summary: string;
  interview_scheduled_at: string;
  evaluator_notes: string;
  rejection_reason: string;
}

export interface OfferFormState {
  application_id: string;
  compensation_amount: string | number;
  currency: string;
  proposed_start_date: string;
  expires_at: string;
  notes_summary: string;
  message: string;
}

export interface OfferLetterTemplateState {
  document_title: string;
  opening_paragraph: string;
  body_paragraph: string;
  closing_paragraph: string;
  signatory_name: string;
  signatory_title: string;
}

export interface RecruitingPageProps {
  form: RecruitingFormState;
  setForm: Dispatch<SetStateAction<RecruitingFormState>>;
  candidateForm: CandidateFormState;
  setCandidateForm: Dispatch<SetStateAction<CandidateFormState>>;
  applicationForm: ApplicationFormState;
  setApplicationForm: Dispatch<SetStateAction<ApplicationFormState>>;
  stageForm: ApplicationStageFormState;
  setStageForm: Dispatch<SetStateAction<ApplicationStageFormState>>;
  offerForm: OfferFormState;
  setOfferForm: Dispatch<SetStateAction<OfferFormState>>;
  requisitions: RecruitingRequisition[];
  candidates: RecruitingCandidate[];
  applications: RecruitingApplication[];
  offers: RecruitingOffer[];
  onboardingHandoffs: RecruitingOnboardingHandoff[];
  selectedOnboardingHandoffId: string;
  setSelectedOnboardingHandoffId: (value: string) => void;
  onboardingTasks: OnboardingChecklistTask[];
  onboardingDocuments: OnboardingDocument[];
  onboardingTaskForm: OnboardingTaskFormState;
  setOnboardingTaskForm: Dispatch<SetStateAction<OnboardingTaskFormState>>;
  onboardingUserForm: OnboardingUserFormState;
  setOnboardingUserForm: Dispatch<SetStateAction<OnboardingUserFormState>>;
  onboardingDocumentForm: OnboardingDocumentFormState;
  setOnboardingDocumentForm: Dispatch<SetStateAction<OnboardingDocumentFormState>>;
  onboardingChecklistTemplates: OnboardingChecklistTemplate[];
  setOnboardingChecklistTemplates: Dispatch<SetStateAction<OnboardingChecklistTemplate[]>>;
  onboardingSummary: OnboardingSummary | null;
  offerLetterTemplate: OfferLetterTemplateState;
  setOfferLetterTemplate: Dispatch<SetStateAction<OfferLetterTemplateState>>;
  departments: RecruitingDepartment[];
  positions: RecruitingPosition[];
  canManage: boolean;
  canApprove: boolean;
  isSubmitting: boolean;
  isCandidateSubmitting: boolean;
  isApplicationSubmitting: boolean;
  isStageSubmitting: boolean;
  isOfferSubmitting: boolean;
  isOfferTemplateSubmitting: boolean;
  isOnboardingTemplateSubmitting: boolean;
  isOnboardingTaskSubmitting: boolean;
  isOnboardingUserSubmitting: boolean;
  isOnboardingDocumentSubmitting: boolean;
  onSubmit: FormEventHandler<HTMLFormElement>;
  onCandidateSubmit: FormEventHandler<HTMLFormElement>;
  onApplicationSubmit: FormEventHandler<HTMLFormElement>;
  onStageSubmit: FormEventHandler<HTMLFormElement>;
  onOfferSubmit: FormEventHandler<HTMLFormElement>;
  onOfferAction: (offerId: number, action: 'requestApproval' | 'approveSend' | 'rejectSend' | 'accept' | 'decline') => void;
  onOfferLetterDownload: (offerId: number, candidateName: string) => void;
  onOnboardingHandoffAction: (handoffId: number, action: 'start' | 'complete') => void;
  onOnboardingTaskSubmit: FormEventHandler<HTMLFormElement>;
  onOnboardingTaskComplete: (taskId: number) => void;
  onOnboardingUserProvision: FormEventHandler<HTMLFormElement>;
  onOnboardingDocumentSubmit: FormEventHandler<HTMLFormElement>;
  onOnboardingDocumentDownload: (documentId: number, fileName: string) => void;
  onOnboardingTemplateSubmit: FormEventHandler<HTMLFormElement>;
  onOfferTemplateSubmit: FormEventHandler<HTMLFormElement>;
  onTransition: (requisitionId: number, action: 'open' | 'hold' | 'close') => void;
  activeLanguage: string;
}

const statusLabel: Record<string, string> = {
  draft: 'Draft',
  open: 'Open',
  on_hold: 'On hold',
  closed: 'Closed',
};

const offerStatusLabel: Record<string, string> = {
  draft: 'Draft',
  sent: 'Sent',
  accepted: 'Accepted',
  declined: 'Declined',
};

const approvalStatusLabel: Record<string, string> = {
  pending: 'Pending approval',
  approved: 'Approved',
  rejected: 'Rejected',
};

function formatOfferAmount(offer: RecruitingOffer, activeLanguage: string): string {
  if (offer.currency === 'ETB') {
    return formatCurrency(offer.compensation_amount, activeLanguage);
  }
  return `${offer.currency} ${offer.compensation_amount.toLocaleString()}`;
}

const RecruitingPage: FC<RecruitingPageProps> = ({
  form,
  setForm,
  candidateForm,
  setCandidateForm,
  applicationForm,
  setApplicationForm,
  stageForm,
  setStageForm,
  offerForm,
  setOfferForm,
  requisitions,
  candidates,
  applications,
  offers,
  onboardingHandoffs,
  selectedOnboardingHandoffId,
  setSelectedOnboardingHandoffId,
  onboardingTasks,
  onboardingDocuments,
  onboardingTaskForm,
  setOnboardingTaskForm,
  onboardingUserForm,
  setOnboardingUserForm,
  onboardingDocumentForm,
  setOnboardingDocumentForm,
  onboardingChecklistTemplates,
  setOnboardingChecklistTemplates,
  onboardingSummary,
  offerLetterTemplate,
  setOfferLetterTemplate,
  departments,
  positions,
  canManage,
  canApprove,
  isSubmitting,
  isCandidateSubmitting,
  isApplicationSubmitting,
  isStageSubmitting,
  isOfferSubmitting,
  isOfferTemplateSubmitting,
  isOnboardingTemplateSubmitting,
  isOnboardingTaskSubmitting,
  isOnboardingUserSubmitting,
  isOnboardingDocumentSubmitting,
  onSubmit,
  onCandidateSubmit,
  onApplicationSubmit,
  onStageSubmit,
  onOfferSubmit,
  onOfferAction,
  onOfferLetterDownload,
  onOnboardingHandoffAction,
  onOnboardingTaskSubmit,
  onOnboardingTaskComplete,
  onOnboardingUserProvision,
  onOnboardingDocumentSubmit,
  onOnboardingDocumentDownload,
  onOnboardingTemplateSubmit,
  onOfferTemplateSubmit,
  onTransition,
  activeLanguage,
}) => {
  const selectedOnboardingHandoff = onboardingHandoffs.find((handoff) => String(handoff.id) === String(selectedOnboardingHandoffId));

  return (
  <>
    {canManage ? (
      <form className="card" onSubmit={onSubmit}>
        <h2>Recruiting requisitions</h2>
        <div className="split">
          <label>
            Slug
            <input
              value={form.slug}
              onChange={(event) => setForm((current) => ({ ...current, slug: event.target.value }))}
            />
          </label>
          <label>
            Title
            <input
              value={form.title}
              onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))}
            />
          </label>
        </div>

        <div className="split split-three">
          <label>
            Department
            <select
              value={form.department_id}
              onChange={(event) => setForm((current) => ({ ...current, department_id: event.target.value }))}
            >
              <option value="">Unassigned</option>
              {departments.map((department) => (
                <option key={department.id} value={department.id}>
                  {department.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Position
            <select
              value={form.position_id}
              onChange={(event) => setForm((current) => ({ ...current, position_id: event.target.value }))}
            >
              <option value="">Unassigned</option>
              {positions.map((position) => (
                <option key={position.id} value={position.id}>
                  {position.title} ({position.job_code})
                </option>
              ))}
            </select>
          </label>
          <label>
            Employment type
            <select
              value={form.employment_type}
              onChange={(event) => setForm((current) => ({ ...current, employment_type: event.target.value }))}
            >
              <option value="full_time">Full time</option>
              <option value="contract">Contract</option>
              <option value="part_time">Part time</option>
            </select>
          </label>
        </div>

        <div className="split split-three">
          <label>
            Location
            <input
              value={form.location}
              onChange={(event) => setForm((current) => ({ ...current, location: event.target.value }))}
            />
          </label>
          <label>
            Headcount
            <input
              type="number"
              min="1"
              value={form.headcount}
              onChange={(event) => setForm((current) => ({ ...current, headcount: event.target.value }))}
            />
          </label>
          <label>
            Target start date
            <input
              type="date"
              value={form.target_start_date}
              onChange={(event) => setForm((current) => ({ ...current, target_start_date: event.target.value }))}
            />
          </label>
        </div>

        <div className="split">
          <label>
            Min budget
            <input
              type="number"
              min="0"
              step="0.01"
              value={form.budgeted_salary_min}
              onChange={(event) => setForm((current) => ({ ...current, budgeted_salary_min: event.target.value }))}
            />
          </label>
          <label>
            Max budget
            <input
              type="number"
              min="0"
              step="0.01"
              value={form.budgeted_salary_max}
              onChange={(event) => setForm((current) => ({ ...current, budgeted_salary_max: event.target.value }))}
            />
          </label>
        </div>

        <label>
          Description
          <textarea
            rows={4}
            value={form.description}
            onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
          />
        </label>

        <button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Saving…' : 'Create requisition'}
        </button>
      </form>
    ) : null}

    {canManage ? (
      <form className="card" onSubmit={onCandidateSubmit}>
        <h2>Candidates</h2>
        <div className="split">
          <label>
            Full name
            <input
              value={candidateForm.full_name}
              onChange={(event) => setCandidateForm((current) => ({ ...current, full_name: event.target.value }))}
            />
          </label>
          <label>
            Email
            <input
              type="email"
              value={candidateForm.email}
              onChange={(event) => setCandidateForm((current) => ({ ...current, email: event.target.value }))}
            />
          </label>
        </div>

        <div className="split split-three">
          <label>
            Phone
            <input
              value={candidateForm.phone}
              onChange={(event) => setCandidateForm((current) => ({ ...current, phone: event.target.value }))}
            />
          </label>
          <label>
            Source
            <input
              value={candidateForm.source}
              onChange={(event) => setCandidateForm((current) => ({ ...current, source: event.target.value }))}
            />
          </label>
          <label>
            Current title
            <input
              value={candidateForm.current_title}
              onChange={(event) => setCandidateForm((current) => ({ ...current, current_title: event.target.value }))}
            />
          </label>
        </div>

        <div className="split">
          <label>
            Current company
            <input
              value={candidateForm.current_company}
              onChange={(event) => setCandidateForm((current) => ({ ...current, current_company: event.target.value }))}
            />
          </label>
          <label>
            Resume URL
            <input
              value={candidateForm.resume_url}
              onChange={(event) => setCandidateForm((current) => ({ ...current, resume_url: event.target.value }))}
            />
          </label>
        </div>

        <button type="submit" disabled={isCandidateSubmitting}>
          {isCandidateSubmitting ? 'Saving…' : 'Create candidate'}
        </button>
      </form>
    ) : null}

    {canManage ? (
      <form className="card" onSubmit={onStageSubmit}>
        <h2>Application stage updates</h2>
        <div className="split split-three">
          <label>
            Application
            <select
              value={stageForm.application_id}
              onChange={(event) => {
                const nextApplicationId = event.target.value;
                const selected = applications.find((application) => application.id === Number(nextApplicationId));
                setStageForm({
                  application_id: nextApplicationId,
                  stage: selected?.stage || 'screening',
                  score: selected?.score ?? '',
                  notes_summary: selected?.notes_summary || '',
                  interview_scheduled_at: selected?.interview_scheduled_at || '',
                  evaluator_notes: selected?.evaluator_notes || '',
                  rejection_reason: selected?.rejection_reason || '',
                });
              }}
            >
              <option value="">Select application</option>
              {applications.map((application) => (
                <option key={application.id} value={application.id}>
                  {application.candidate_name} - {application.requisition_title}
                </option>
              ))}
            </select>
          </label>
          <label>
            Stage
            <select
              value={stageForm.stage}
              onChange={(event) => setStageForm((current) => ({ ...current, stage: event.target.value }))}
            >
              <option value="screening">Screening</option>
              <option value="interview">Interview</option>
              <option value="offer">Offer</option>
              <option value="hired">Hired</option>
              <option value="rejected">Rejected</option>
              <option value="withdrawn">Withdrawn</option>
            </select>
          </label>
          <label>
            Score
            <input
              type="number"
              min="0"
              max="100"
              value={stageForm.score}
              onChange={(event) => setStageForm((current) => ({ ...current, score: event.target.value }))}
            />
          </label>
        </div>

        <div className="split">
          <label>
            Interview schedule
            <input
              type="datetime-local"
              value={stageForm.interview_scheduled_at}
              onChange={(event) => setStageForm((current) => ({ ...current, interview_scheduled_at: event.target.value }))}
            />
          </label>
          <label>
            Rejection reason
            <input
              value={stageForm.rejection_reason}
              onChange={(event) => setStageForm((current) => ({ ...current, rejection_reason: event.target.value }))}
            />
          </label>
        </div>

        <label>
          Notes summary
          <textarea
            rows={3}
            value={stageForm.notes_summary}
            onChange={(event) => setStageForm((current) => ({ ...current, notes_summary: event.target.value }))}
          />
        </label>

        <label>
          Evaluator notes
          <textarea
            rows={4}
            value={stageForm.evaluator_notes}
            onChange={(event) => setStageForm((current) => ({ ...current, evaluator_notes: event.target.value }))}
          />
        </label>

        <button type="submit" disabled={isStageSubmitting || !stageForm.application_id}>
          {isStageSubmitting ? 'Saving…' : 'Update application stage'}
        </button>
      </form>
    ) : null}

    {canManage ? (
      <form className="card" onSubmit={onOfferTemplateSubmit}>
        <h2>Offer letter template</h2>
        <label>
          Document title
          <input
            value={offerLetterTemplate.document_title}
            onChange={(event) => setOfferLetterTemplate((current) => ({ ...current, document_title: event.target.value }))}
          />
        </label>

        <label>
          Opening paragraph
          <textarea
            rows={3}
            value={offerLetterTemplate.opening_paragraph}
            onChange={(event) => setOfferLetterTemplate((current) => ({ ...current, opening_paragraph: event.target.value }))}
          />
        </label>

        <label>
          Body paragraph
          <textarea
            rows={5}
            value={offerLetterTemplate.body_paragraph}
            onChange={(event) => setOfferLetterTemplate((current) => ({ ...current, body_paragraph: event.target.value }))}
          />
        </label>

        <label>
          Closing paragraph
          <textarea
            rows={3}
            value={offerLetterTemplate.closing_paragraph}
            onChange={(event) => setOfferLetterTemplate((current) => ({ ...current, closing_paragraph: event.target.value }))}
          />
        </label>

        <div className="split">
          <label>
            Signatory name
            <input
              value={offerLetterTemplate.signatory_name}
              onChange={(event) => setOfferLetterTemplate((current) => ({ ...current, signatory_name: event.target.value }))}
            />
          </label>
          <label>
            Signatory title
            <input
              value={offerLetterTemplate.signatory_title}
              onChange={(event) => setOfferLetterTemplate((current) => ({ ...current, signatory_title: event.target.value }))}
            />
          </label>
        </div>

        <p className="helper">Available placeholders: {'{candidate_name}'}, {'{company_name}'}, {'{requisition_title}'}, {'{employment_type}'}, {'{location}'}, {'{start_date}'}, {'{offer_expiry}'}, {'{compensation}'}, {'{notes_summary}'}</p>

        <button type="submit" disabled={isOfferTemplateSubmitting}>
          {isOfferTemplateSubmitting ? 'Saving…' : 'Save template'}
        </button>
      </form>
    ) : null}

    {canManage ? (
      <form className="card" onSubmit={onOnboardingTemplateSubmit}>
        <h2>Onboarding checklist templates</h2>
        <p className="helper">These tasks are copied into each completed onboarding handoff for this company.</p>
        {onboardingChecklistTemplates.map((template, index) => (
          <div className="subcard" key={`${template.id || 'template'}-${index}`}>
            <div className="split split-three">
              <label>
                Task title
                <input
                  value={template.title}
                  onChange={(event) => setOnboardingChecklistTemplates((current) => current.map((item, itemIndex) => (
                    itemIndex === index ? { ...item, title: event.target.value } : item
                  )))}
                />
              </label>
              <label>
                Due days offset
                <input
                  type="number"
                  value={template.due_days_offset ?? ''}
                  onChange={(event) => setOnboardingChecklistTemplates((current) => current.map((item, itemIndex) => (
                    itemIndex === index ? { ...item, due_days_offset: event.target.value } : item
                  )))}
                />
              </label>
              <label>
                Required
                <input
                  type="checkbox"
                  checked={template.is_required}
                  onChange={(event) => setOnboardingChecklistTemplates((current) => current.map((item, itemIndex) => (
                    itemIndex === index ? { ...item, is_required: event.target.checked } : item
                  )))}
                />
              </label>
            </div>
            <div className="split">
              <label>
                Acknowledgement version
                <input
                  value={template.acknowledgement_version || ''}
                  onChange={(event) => setOnboardingChecklistTemplates((current) => current.map((item, itemIndex) => (
                    itemIndex === index ? { ...item, acknowledgement_version: event.target.value } : item
                  )))}
                />
              </label>
              <label>
                Acknowledgement statement
                <textarea
                  rows={3}
                  value={template.acknowledgement_statement || ''}
                  onChange={(event) => setOnboardingChecklistTemplates((current) => current.map((item, itemIndex) => (
                    itemIndex === index ? { ...item, acknowledgement_statement: event.target.value } : item
                  )))}
                />
              </label>
            </div>
            <div className="button-row">
              <button
                type="button"
                className="secondary-button"
                onClick={() => setOnboardingChecklistTemplates((current) => current.filter((_, itemIndex) => itemIndex !== index))}
                disabled={onboardingChecklistTemplates.length <= 1}
              >
                Remove
              </button>
            </div>
          </div>
        ))}
        <div className="button-row">
          <button
            type="button"
            className="secondary-button"
            onClick={() => setOnboardingChecklistTemplates((current) => ([
              ...current,
              { title: '', due_days_offset: '', sort_order: current.length, is_required: true, acknowledgement_statement: '', acknowledgement_version: 'v1' },
            ]))}
          >
            Add checklist task
          </button>
          <button type="submit" disabled={isOnboardingTemplateSubmitting}>
            {isOnboardingTemplateSubmitting ? 'Saving…' : 'Save checklist templates'}
          </button>
        </div>
      </form>
    ) : null}

    {canManage ? (
      <form className="card" onSubmit={onOfferSubmit}>
        <h2>Offers</h2>
        <div className="split split-three">
          <label>
            Application
            <select
              value={offerForm.application_id}
              onChange={(event) => setOfferForm((current) => ({ ...current, application_id: event.target.value }))}
            >
              <option value="">Select application</option>
              {applications
                .filter((application) => application.status === 'active' && ['interview', 'offer'].includes(application.stage))
                .map((application) => (
                  <option key={application.id} value={application.id}>
                    {application.candidate_name} - {application.requisition_title}
                  </option>
                ))}
            </select>
          </label>
          <label>
            Compensation
            <input
              type="number"
              min="0"
              step="0.01"
              value={offerForm.compensation_amount}
              onChange={(event) => setOfferForm((current) => ({ ...current, compensation_amount: event.target.value }))}
            />
          </label>
          <label>
            Currency
            <input
              value={offerForm.currency}
              onChange={(event) => setOfferForm((current) => ({ ...current, currency: event.target.value.toUpperCase() }))}
            />
          </label>
        </div>

        <div className="split">
          <label>
            Proposed start date
            <input
              type="date"
              value={offerForm.proposed_start_date}
              onChange={(event) => setOfferForm((current) => ({ ...current, proposed_start_date: event.target.value }))}
            />
          </label>
          <label>
            Offer expiry
            <input
              type="datetime-local"
              value={offerForm.expires_at}
              onChange={(event) => setOfferForm((current) => ({ ...current, expires_at: event.target.value }))}
            />
          </label>
        </div>

        <label>
          Notes summary
          <textarea
            rows={3}
            value={offerForm.notes_summary}
            onChange={(event) => setOfferForm((current) => ({ ...current, notes_summary: event.target.value }))}
          />
        </label>

        <label>
          Offer message
          <textarea
            rows={4}
            value={offerForm.message}
            onChange={(event) => setOfferForm((current) => ({ ...current, message: event.target.value }))}
          />
        </label>

        <button type="submit" disabled={isOfferSubmitting || !offerForm.application_id}>
          {isOfferSubmitting ? 'Saving…' : 'Create offer'}
        </button>
      </form>
    ) : null}

    {canManage ? (
      <form className="card" onSubmit={onApplicationSubmit}>
        <h2>Candidate applications</h2>
        <div className="split split-three">
          <label>
            Requisition
            <select
              value={applicationForm.requisition_id}
              onChange={(event) => setApplicationForm((current) => ({ ...current, requisition_id: event.target.value }))}
            >
              <option value="">Select requisition</option>
              {requisitions
                .filter((requisition) => requisition.status === 'open')
                .map((requisition) => (
                  <option key={requisition.id} value={requisition.id}>
                    {requisition.title}
                  </option>
                ))}
            </select>
          </label>
          <label>
            Candidate
            <select
              value={applicationForm.candidate_id}
              onChange={(event) => setApplicationForm((current) => ({ ...current, candidate_id: event.target.value }))}
            >
              <option value="">Select candidate</option>
              {candidates.map((candidate) => (
                <option key={candidate.id} value={candidate.id}>
                  {candidate.full_name} ({candidate.email})
                </option>
              ))}
            </select>
          </label>
          <label>
            Score
            <input
              type="number"
              min="0"
              max="100"
              value={applicationForm.score}
              onChange={(event) => setApplicationForm((current) => ({ ...current, score: event.target.value }))}
            />
          </label>
        </div>

        <label>
          Notes
          <textarea
            rows={3}
            value={applicationForm.notes_summary}
            onChange={(event) => setApplicationForm((current) => ({ ...current, notes_summary: event.target.value }))}
          />
        </label>

        <button type="submit" disabled={isApplicationSubmitting}>
          {isApplicationSubmitting ? 'Saving…' : 'Create application'}
        </button>
      </form>
    ) : null}

    <section className="card list-card">
      <h2>Open recruiting workspace</h2>
      {requisitions.length ? (
        <ul className="list compact-list">
          {requisitions.map((requisition) => (
            <li key={requisition.id}>
              <div>
                <strong>{requisition.title}</strong>
                <span>
                  {statusLabel[requisition.status] || requisition.status}
                  {requisition.department_name ? ` • ${requisition.department_name}` : ''}
                  {requisition.position_title ? ` • ${requisition.position_title}` : ''}
                  {requisition.location ? ` • ${requisition.location}` : ''}
                  {` • ${formatCurrency(requisition.budgeted_salary_min, activeLanguage)} - ${formatCurrency(requisition.budgeted_salary_max, activeLanguage)}`}
                </span>
              </div>
              {canManage ? (
                <div className="button-row">
                  {requisition.status === 'draft' || requisition.status === 'on_hold' ? (
                    <button type="button" className="secondary-button" onClick={() => onTransition(requisition.id, 'open')}>
                      Open
                    </button>
                  ) : null}
                  {requisition.status === 'open' ? (
                    <button type="button" className="secondary-button" onClick={() => onTransition(requisition.id, 'hold')}>
                      Hold
                    </button>
                  ) : null}
                  {requisition.status !== 'closed' ? (
                    <button type="button" className="secondary-button" onClick={() => onTransition(requisition.id, 'close')}>
                      Close
                    </button>
                  ) : null}
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <p>No requisitions yet.</p>
      )}
    </section>

    <section className="card list-card">
      <h2>Candidate register</h2>
      {candidates.length ? (
        <ul className="list compact-list">
          {candidates.map((candidate) => (
            <li key={candidate.id}>
              <div>
                <strong>{candidate.full_name}</strong>
                <span>
                  {candidate.email}
                  {candidate.source ? ` • ${candidate.source}` : ''}
                  {candidate.current_title ? ` • ${candidate.current_title}` : ''}
                  {candidate.current_company ? ` • ${candidate.current_company}` : ''}
                </span>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p>No candidates yet.</p>
      )}
    </section>

    <section className="card list-card">
      <h2>Applications pipeline</h2>
      {applications.length ? (
        <ul className="list compact-list">
          {applications.map((application) => (
            <li key={application.id}>
              <div>
                <strong>{application.candidate_name}</strong>
                <span>
                  {application.requisition_title}
                  {` • ${application.stage}`}
                  {application.score !== null && application.score !== undefined ? ` • score ${application.score}` : ''}
                  {application.notes_summary ? ` • ${application.notes_summary}` : ''}
                  {application.interview_scheduled_at ? ` • interview ${application.interview_scheduled_at}` : ''}
                  {application.rejection_reason ? ` • ${application.rejection_reason}` : ''}
                </span>
                {application.evaluator_notes ? <span>{application.evaluator_notes}</span> : null}
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p>No applications yet.</p>
      )}
    </section>

    <section className="card list-card">
      <h2>Offer desk</h2>
      {offers.length ? (
        <ul className="list compact-list">
          {offers.map((offer) => (
            <li key={offer.id}>
              <div>
                <strong>{offer.candidate_name}</strong>
                <span>
                  {offer.requisition_title}
                  {` • ${offerStatusLabel[offer.status] || offer.status}`}
                  {` • ${formatOfferAmount(offer, activeLanguage)}`}
                  {offer.send_approval_status ? ` • ${approvalStatusLabel[offer.send_approval_status] || offer.send_approval_status}` : ''}
                  {offer.employee_code ? ` • employee ${offer.employee_code}` : ''}
                  {offer.proposed_start_date ? ` • starts ${offer.proposed_start_date}` : ''}
                  {offer.expires_at ? ` • expires ${offer.expires_at}` : ''}
                </span>
                {offer.notes_summary ? <span>{offer.notes_summary}</span> : null}
                {offer.message ? <span>{offer.message}</span> : null}
                {offer.send_approval_decision_reason ? <span>{offer.send_approval_decision_reason}</span> : null}
              </div>
              {canManage || canApprove ? (
                <div className="button-row">
                  {canManage && offer.status === 'draft' && !offer.send_approval_status ? (
                    <button type="button" className="secondary-button" onClick={() => onOfferAction(offer.id, 'requestApproval')}>
                      Request approval
                    </button>
                  ) : null}
                  {canApprove && offer.send_approval_status === 'pending' ? (
                    <button type="button" className="secondary-button" onClick={() => onOfferAction(offer.id, 'approveSend')}>
                      Approve send
                    </button>
                  ) : null}
                  {canApprove && offer.send_approval_status === 'pending' ? (
                    <button type="button" className="secondary-button" onClick={() => onOfferAction(offer.id, 'rejectSend')}>
                      Reject send
                    </button>
                  ) : null}
                  {canManage && offer.status === 'sent' ? (
                    <button type="button" className="secondary-button" onClick={() => onOfferAction(offer.id, 'accept')}>
                      Mark accepted
                    </button>
                  ) : null}
                  {canManage && offer.status === 'sent' ? (
                    <button type="button" className="secondary-button" onClick={() => onOfferAction(offer.id, 'decline')}>
                      Mark declined
                    </button>
                  ) : null}
                  {offer.send_approval_status === 'approved' ? (
                    <button type="button" className="secondary-button" onClick={() => onOfferLetterDownload(offer.id, offer.candidate_name)}>
                      Download letter
                    </button>
                  ) : null}
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <p>No offers yet.</p>
      )}
    </section>

    <section className="card list-card">
      <h2>Onboarding handoff queue</h2>
      {onboardingHandoffs.length ? (
        <ul className="list compact-list">
          {onboardingHandoffs.map((handoff) => (
            <li key={handoff.id}>
              <div>
                <strong>{handoff.candidate_name}</strong>
                <span>
                  {handoff.requisition_title}
                  {` • ${handoff.status}`}
                  {` • owner ${handoff.owner_role}`}
                  {handoff.employee_code ? ` • employee ${handoff.employee_code}` : ''}
                  {handoff.target_start_date ? ` • start ${handoff.target_start_date}` : ''}
                </span>
                {handoff.notes_summary ? <span>{handoff.notes_summary}</span> : null}
              </div>
              {canManage ? (
                <div className="button-row">
                  {handoff.status === 'pending' ? (
                    <button type="button" className="secondary-button" onClick={() => onOnboardingHandoffAction(handoff.id, 'start')}>
                      Start
                    </button>
                  ) : null}
                  {handoff.status === 'in_progress' ? (
                    <button type="button" className="secondary-button" onClick={() => onOnboardingHandoffAction(handoff.id, 'complete')}>
                      Complete
                    </button>
                  ) : null}
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <p>No onboarding handoffs yet.</p>
      )}
    </section>

    {onboardingSummary ? (
      <section className="card list-card">
        <h2>Onboarding progress summary</h2>
        <div className="split split-three">
          <p><strong>{onboardingSummary.total_handoffs}</strong><br />handoffs</p>
          <p><strong>{onboardingSummary.completed_handoffs}</strong><br />completed</p>
          <p><strong>{onboardingSummary.provisioned_users}</strong><br />provisioned users</p>
        </div>
        <div className="split split-three">
          <p><strong>{onboardingSummary.total_tasks}</strong><br />total tasks</p>
          <p><strong>{onboardingSummary.completed_tasks}</strong><br />completed tasks</p>
          <p><strong>{onboardingSummary.total_documents}</strong><br />documents</p>
        </div>
        <div className="split split-three">
          <p><strong>{onboardingSummary.required_acknowledgements_pending}</strong><br />pending acknowledgements</p>
          <p><strong>{onboardingSummary.required_acknowledgements_completed}</strong><br />completed acknowledgements</p>
          <p><strong>{onboardingSummary.overdue_tasks}</strong><br />overdue tasks</p>
        </div>
        {onboardingSummary.items.length ? (
          <ul className="list compact-list">
            {onboardingSummary.items.map((item) => (
              <li key={item.handoff_id}>
                <div>
                  <strong>{item.candidate_name}</strong>
                  <span>
                    {item.requisition_title}
                    {` • ${item.status}`}
                    {item.employee_code ? ` • ${item.employee_code}` : ''}
                    {item.provisioned_username ? ` • ${item.provisioned_username}` : ''}
                  </span>
                  <span>
                    {`Tasks ${item.task_completed}/${item.task_total}`}
                    {` • Documents ${item.document_total}`}
                    {` • Ack pending ${item.required_acknowledgements_pending}`}
                    {` • Ack complete ${item.required_acknowledgements_completed}`}
                    {item.overdue_task_count ? ` • Overdue ${item.overdue_task_count}` : ''}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="helper">No onboarding handoffs yet.</p>
        )}
      </section>
    ) : null}

    {canManage ? (
      <section className="card list-card">
        <h2>Onboarding details</h2>
        <label>
          Handoff
          <select value={selectedOnboardingHandoffId} onChange={(event) => setSelectedOnboardingHandoffId(event.target.value)}>
            <option value="">Select onboarding handoff</option>
            {onboardingHandoffs.map((handoff) => (
              <option key={handoff.id} value={handoff.id}>
                {handoff.candidate_name} - {handoff.requisition_title}
              </option>
            ))}
          </select>
        </label>

        {selectedOnboardingHandoff ? (
          <>
            <p className="helper">
              Status: {selectedOnboardingHandoff.status}
              {selectedOnboardingHandoff.employee_code ? ` • employee ${selectedOnboardingHandoff.employee_code}` : ''}
              {selectedOnboardingHandoff.provisioned_username ? ` • user ${selectedOnboardingHandoff.provisioned_username}` : ''}
            </p>

            {selectedOnboardingHandoff.employee_id && !selectedOnboardingHandoff.provisioned_username ? (
              <form className="subcard" onSubmit={onOnboardingUserProvision}>
                <h3>Provision self-service user</h3>
                <div className="split split-three">
                  <label>
                    Username
                    <input
                      value={onboardingUserForm.username}
                      onChange={(event) => setOnboardingUserForm((current) => ({ ...current, username: event.target.value }))}
                    />
                  </label>
                  <label>
                    Temporary password
                    <input
                      type="password"
                      value={onboardingUserForm.password}
                      onChange={(event) => setOnboardingUserForm((current) => ({ ...current, password: event.target.value }))}
                    />
                  </label>
                  <label>
                    Full name
                    <input
                      value={onboardingUserForm.full_name}
                      onChange={(event) => setOnboardingUserForm((current) => ({ ...current, full_name: event.target.value }))}
                    />
                  </label>
                </div>
                <button type="submit" disabled={isOnboardingUserSubmitting}>
                  {isOnboardingUserSubmitting ? 'Provisioning…' : 'Provision user'}
                </button>
              </form>
            ) : null}

            <form className="subcard" onSubmit={onOnboardingTaskSubmit}>
              <h3>Checklist</h3>
              <div className="split">
                <label>
                  Task title
                  <input
                    value={onboardingTaskForm.title}
                    onChange={(event) => setOnboardingTaskForm((current) => ({ ...current, title: event.target.value }))}
                  />
                </label>
                <label>
                  Due date
                  <input
                    type="date"
                    value={onboardingTaskForm.due_date}
                    onChange={(event) => setOnboardingTaskForm((current) => ({ ...current, due_date: event.target.value }))}
                  />
                </label>
              </div>
              <button type="submit" disabled={isOnboardingTaskSubmitting || !selectedOnboardingHandoff.employee_id}>
                {isOnboardingTaskSubmitting ? 'Saving…' : 'Add task'}
              </button>
              {onboardingTasks.length ? (
                <ul className="list compact-list">
                  {onboardingTasks.map((task) => (
                    <li key={task.id}>
                      <div>
                        <strong>{task.title}</strong>
                        <span>
                          {task.is_required ? 'requires acknowledgement • ' : ''}
                          {task.status}
                          {task.due_date ? ` • due ${task.due_date}` : ''}
                          {task.acknowledgement_version ? ` • policy ${task.acknowledgement_version}` : ''}
                          {task.acknowledged_signature_name ? ` • signed ${task.acknowledged_signature_name}` : ''}
                          {task.acknowledged_at ? ` • acknowledged ${task.acknowledged_at}` : ''}
                          {task.completed_at ? ` • completed ${task.completed_at}` : ''}
                        </span>
                        {task.is_required && task.acknowledgement_statement ? <span>{task.acknowledgement_statement}</span> : null}
                      </div>
                      {task.status !== 'completed' ? (
                        <div className="button-row">
                          <button type="button" className="secondary-button" onClick={() => onOnboardingTaskComplete(task.id)}>
                            Mark complete
                          </button>
                        </div>
                      ) : null}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="helper">No checklist tasks yet.</p>
              )}
            </form>

            <form className="subcard" onSubmit={onOnboardingDocumentSubmit}>
              <h3>Documents</h3>
              <div className="split split-three">
                <label>
                  Document name
                  <input
                    value={onboardingDocumentForm.document_name}
                    onChange={(event) => setOnboardingDocumentForm((current) => ({ ...current, document_name: event.target.value }))}
                  />
                </label>
                <label>
                  Related task
                  <select
                    value={onboardingDocumentForm.task_id}
                    onChange={(event) => setOnboardingDocumentForm((current) => ({ ...current, task_id: event.target.value }))}
                  >
                    <option value="">General document</option>
                    {onboardingTasks.map((task) => (
                      <option key={task.id} value={task.id}>
                        {task.title}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  File
                  <input
                    type="file"
                    onChange={(event) => setOnboardingDocumentForm((current) => ({ ...current, file: event.target.files?.[0] || null }))}
                  />
                </label>
              </div>
              <button type="submit" disabled={isOnboardingDocumentSubmitting || !selectedOnboardingHandoff.employee_id}>
                {isOnboardingDocumentSubmitting ? 'Uploading…' : 'Upload document'}
              </button>
              {onboardingDocuments.length ? (
                <ul className="list compact-list">
                  {onboardingDocuments.map((document) => (
                    <li key={document.id}>
                      <div>
                        <strong>{document.document_name}</strong>
                        <span>
                          {document.file_name}
                          {` • ${document.content_type}`}
                          {` • ${document.size_bytes} bytes`}
                        </span>
                      </div>
                      <div className="button-row">
                        <button type="button" className="secondary-button" onClick={() => onOnboardingDocumentDownload(document.id, document.file_name)}>
                          Download
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="helper">No onboarding documents yet.</p>
              )}
            </form>
          </>
        ) : (
          <p className="helper">Select a handoff to manage onboarding tasks, documents, and self-service access.</p>
        )}
      </section>
    ) : null}
  </>
  );
};

export default RecruitingPage;
